/**
 * In-memory store for the access token injected by the shell via postMessage.
 *
 * When running inside the shell iframe the token is supplied via postMessage.
 * When running standalone or in tests, we fall back to Amplify's
 * `fetchAuthSession()` so existing auth mocks and local sessions still work.
 *
 * Refresh strategy: we treat the cached token as authoritative *until* it is
 * within `EXPIRY_BUFFER_SECONDS` of expiring. At that point we proactively
 * call `fetchAuthSession()` (Amplify's cookie storage is shared across the
 * `.ledidi.no` domain with the shell, so the iframe Amplify can refresh the
 * same Cognito session). If Amplify can't reach a session — e.g. cookies
 * partitioned in a third-party iframe context — we fall back to returning
 * the cached (potentially stale) token so the backend still returns a normal
 * 401 instead of the request silently failing client-side.
 */
import { fetchAuthSession } from '@aws-amplify/auth';

let _accessToken: string | null = null;

/** Called by CommunicationBridgeProvider when the shell sends a token. */
export function setAccessToken(token: string) {
	_accessToken = token;
}

/** Returns the current access token, or null if not yet received. */
export function getAccessToken(): string | null {
	return _accessToken;
}

/**
 * Decode the JWT payload (no verification – the token was already verified
 * by Cognito when the shell obtained it).
 */
export function decodeTokenPayload(token?: string | null): Record<string, unknown> | null {
	const t = token ?? _accessToken;
	if (!t) return null;
	try {
		const payload = t.split('.')[1];
		return JSON.parse(atob(payload));
	} catch {
		return null;
	}
}

const LEDIDI_USER_ID_CLAIM = 'ledidi:user_id';

/**
 * The Ledidi user id that the Cognito pre-token-generation hook mints into the
 * token. This is the id the shell identifies with in Mixpanel, so using it
 * everywhere makes one person one id across every tool.
 */
export function getUserIdFromToken(token?: string | null): string | null {
	const userId = decodeTokenPayload(token)?.[LEDIDI_USER_ID_CLAIM];
	return typeof userId === 'string' ? userId : null;
}

const SESSION_ID_CLAIM = 'origin_jti';

/**
 * The sign-in this token belongs to. `origin_jti` is minted when the user
 * authenticates and survives every refresh, so it names one sign-in, while
 * `jti` would name one token. The shell reads the same claim and sends it to
 * the backend as `x-session-id`, where it becomes the audit log's correlation
 * id, so an error here lines up with the shell and with the backend.
 */
export function getSessionIdFromToken(token?: string | null): string | null {
	const sessionId = decodeTokenPayload(token)?.[SESSION_ID_CLAIM];
	return typeof sessionId === 'string' ? sessionId : null;
}

const EXPIRY_BUFFER_SECONDS = 30;

const isTokenNearExpiry = (token: string): boolean => {
	const payload = decodeTokenPayload(token);
	if (!payload || typeof payload.exp !== 'number') {
		// Unparseable / no exp claim — treat as expired so we attempt a refresh.
		return true;
	}
	const nowSeconds = Math.floor(Date.now() / 1000);
	return payload.exp - EXPIRY_BUFFER_SECONDS <= nowSeconds;
};

/**
 * Returns a non-expired access token, refreshing via Amplify when the cached
 * one is close to expiry. Falls back to the cached token if Amplify can't
 * provide a fresh one.
 */
export async function getAccessTokenWithFallback(): Promise<string | null> {
	if (_accessToken && !isTokenNearExpiry(_accessToken)) {
		return _accessToken;
	}

	try {
		const session = await fetchAuthSession();
		const refreshed = session.tokens?.idToken?.toString() ?? null;
		if (refreshed) {
			_accessToken = refreshed;
			return refreshed;
		}
	} catch {
		// Fall through to cached value below.
	}

	return _accessToken;
}
